import { useState } from "react";
import { Calculator, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/docstate";

const money = (n) => "AED " + Math.round(n || 0).toLocaleString("en-AE");

// Monthly repayment for a principal at an annual rate over a term in years.
const monthlyPayment = (principal, annualRatePct, years) => {
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

const Slider = ({ label, value, min, max, step, onChange, fmt }) => (
  <div>
    <div className="flex items-center justify-between">
      <label className="text-xs text-[#9D8D83]">{label}</label>
      <span className="text-sm font-semibold text-[#F3E5AB]">{fmt(value)}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
      className="mt-2 w-full accent-[#C9A24B]" data-testid={`mortgage-${label.toLowerCase().replace(/[^a-z]/g, "")}`} />
  </div>
);

export const MortgageCalculator = ({ price = 0, community = "" }) => {
  const [tab, setTab] = useState("pay"); // pay | afford
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(4.5);
  const [term, setTerm] = useState(25);

  // "What can I afford?" inputs + lead capture
  const [budget, setBudget] = useState(15000);
  const [lead, setLead] = useState({ name: "", phone: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const loan = price * (1 - down / 100);
  const monthly = monthlyPayment(loan, rate, term);

  // reverse the formula: from an affordable monthly payment, derive loan then price
  const r = rate / 100 / 12;
  const n = term * 12;
  const affordableLoan = r === 0 ? budget * n : (budget * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
  const affordablePrice = affordableLoan / (1 - down / 100);

  const submitPreApproval = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.phone.trim()) return toast.error("Add your name and phone for a pre-approval estimate.");
    setSending(true);
    try {
      await submitEnquiry({
        name: lead.name, phone: lead.phone, kind: "valuation", subject: "Mortgage pre-approval estimate",
        message: `Budget AED ${budget}/mo → ~${money(affordablePrice)} affordability. Down ${down}%, ${rate}%, ${term}yr.${community ? " Area: " + community : ""}`,
        page: window.location.pathname,
      });
      setDone(true);
      toast.success("Estimate ready — an advisor will confirm your pre-approval.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ds-card p-6" data-testid="mortgage-calculator">
      <div className="flex items-center gap-2">
        <Calculator className="h-5 w-5 text-[#C9A24B]" />
        <h3 className="ds-display text-lg text-white">Mortgage Calculator</h3>
      </div>

      <div className="mt-4 flex rounded-full border border-[#C9A24B]/20 p-0.5 text-xs">
        <button onClick={() => setTab("pay")} data-testid="mortgage-tab-pay" className={`flex-1 rounded-full px-3 py-2 font-semibold transition-colors ${tab === "pay" ? "bg-[#C9A24B] text-[#0B0B0C]" : "text-[#9D8D83]"}`}>Monthly Payment</button>
        <button onClick={() => setTab("afford")} data-testid="mortgage-tab-afford" className={`flex-1 rounded-full px-3 py-2 font-semibold transition-colors ${tab === "afford" ? "bg-[#C9A24B] text-[#0B0B0C]" : "text-[#9D8D83]"}`}>What Can I Afford?</button>
      </div>

      <div className="mt-5 space-y-5">
        <Slider label="Down Payment" value={down} min={10} max={60} step={5} onChange={setDown} fmt={(v) => `${v}%`} />
        <Slider label="Interest Rate" value={rate} min={2} max={8} step={0.1} onChange={setRate} fmt={(v) => `${v.toFixed(1)}%`} />
        <Slider label="Loan Term" value={term} min={5} max={30} step={1} onChange={setTerm} fmt={(v) => `${v} yrs`} />

        {tab === "pay" ? (
          <div className="rounded-xl border border-[#C9A24B]/20 bg-[#0F0F11] p-5 text-center">
            <p className="text-xs uppercase tracking-widest text-[#9D8D83]">Estimated monthly payment</p>
            <p className="ds-display mt-1 text-3xl text-[#F3E5AB]" data-testid="mortgage-monthly">{money(monthly)}</p>
            <div className="mt-3 flex justify-between text-xs text-[#9D8D83]">
              <span>Loan: {money(loan)}</span><span>Deposit: {money(price * down / 100)}</span>
            </div>
          </div>
        ) : done ? (
          <div className="rounded-xl border border-[#C9A24B]/20 bg-[#0F0F11] p-6 text-center" data-testid="mortgage-preapproval-success">
            <CheckCircle2 className="mx-auto h-9 w-9 text-[#C9A24B]" />
            <p className="ds-display mt-2 text-lg text-[#F3E5AB]">You could afford up to {money(affordablePrice)}</p>
            <p className="mt-1 text-sm font-light text-[#9D8D83]">An advisor will confirm your pre-approval shortly.</p>
          </div>
        ) : (
          <form onSubmit={submitPreApproval} className="space-y-4">
            <Slider label="Monthly Budget" value={budget} min={3000} max={150000} step={1000} onChange={setBudget} fmt={(v) => money(v) + "/mo"} />
            <div className="rounded-xl border border-[#C9A24B]/20 bg-[#0F0F11] p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-[#9D8D83]">You could afford around</p>
              <p className="ds-display mt-1 text-2xl text-[#F3E5AB]" data-testid="mortgage-affordable">{money(affordablePrice)}</p>
            </div>
            <input value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))} placeholder="Your name" className="ds-input" data-testid="mortgage-name" />
            <input value={lead.phone} onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))} placeholder="Phone" className="ds-input" data-testid="mortgage-phone" />
            <button type="submit" disabled={sending} className="ds-gold-btn w-full px-5 py-3 text-sm disabled:opacity-60" data-testid="mortgage-preapproval-submit">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{sending ? "Checking..." : "Get my pre-approval estimate"}
            </button>
          </form>
        )}
        <p className="text-[10px] leading-relaxed text-[#6E635B]">Estimates only, not a financial offer. Rates vary by lender and eligibility.</p>
      </div>
    </div>
  );
};
